import * as React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { cn } from '../../lib/cn';

export interface BarDatum {
  label: string;
  value: number;
  /** Optional override fill (e.g. growth scoreboard with red/green). */
  fill?: string;
}

export interface BarChartProps {
  data: BarDatum[];
  orientation?: 'vertical' | 'horizontal';
  /** Format the bar value label (tooltips + on-bar labels — full precision). */
  formatValue?: (n: number) => string;
  /** Format axis tick labels — defaults to compact notation (1.2M / 850K)
   *  to fit narrow YAxis widths. Override only when full precision is
   *  required (e.g. a small-range chart). */
  formatTick?: (n: number) => string;
  /** Single fill or per-bar fill. Used when datum.fill is unset. */
  defaultFill?: string;
  /** Show value labels on bars. Default true for horizontal, false for vertical. */
  showValueLabels?: boolean;
  emptyMessage?: string;
  className?: string;
  height?: number;
  /** Vertical charts: which category labels to draw. Default 0 = every label. Use
   *  'preserveStartEnd' for a long date axis so labels thin out instead of overlapping
   *  on a narrow card (AI Agents hub, 2026-10-05: 14 day labels ran into one another). */
  xTickInterval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
}

const fmtNum = (n: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

// Compact tick formatter — "10M" / "1.5M" / "850K" — fits inside the
// default 56px YAxis width without clipping. The bar tooltip / on-bar
// label still gets the full-precision formatValue.
const fmtTick = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000)     return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
};

export function BarChart({
  data,
  orientation = 'vertical',
  formatValue = fmtNum,
  formatTick = fmtTick,
  defaultFill = 'var(--accent-500)',
  showValueLabels,
  emptyMessage = 'No data',
  className,
  height = 280,
  xTickInterval = 0,
}: BarChartProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-bg-surface text-text-muted',
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  const labels = showValueLabels ?? orientation === 'horizontal';
  // Orchestrator front-end check, 2026-10-02 (lead-origins-v2's "Platform & device" bars): a
  // value label placed to the RIGHT of the bar (recharts' own `position="right"`) clips against
  // the chart's own edge once the bar is long enough — the longest bar in a horizontal chart is
  // exactly the one most likely to hit this, so it was clipping the biggest number on the page
  // ("2,1…" for the longest "By device" bar). Past ~85% of the plotted width there is no longer
  // room outside the bar for the label, so it renders INSIDE the bar's own end instead (anchored
  // from the right, light text for contrast against the fill) rather than reserving a fixed
  // margin sized for the longest possible value, which would shrink every bar's own width instead.
  const maxValue = Math.max(0, ...data.map((d) => d.value));
  const horizontalLabelContent = (props: { x?: number; y?: number; width?: number; height?: number; value?: number }) => {
    const { x = 0, y = 0, width = 0, height = 0, value = 0 } = props;
    const text = formatValue(value);
    const insideBar = maxValue > 0 && value >= maxValue * 0.85;
    return insideBar ? (
      <text x={x + width - 6} y={y + height / 2} dy={4} textAnchor="end" fill="var(--text-on-brand)" fontSize={11}>
        {text}
      </text>
    ) : (
      <text x={x + width + 6} y={y + height / 2} dy={4} textAnchor="start" fill="var(--text-secondary)" fontSize={11}>
        {text}
      </text>
    );
  };
  const barProps = {
    fill: defaultFill,
    radius: orientation === 'horizontal' ? ([0, 4, 4, 0] as [number, number, number, number]) : ([4, 4, 0, 0] as [number, number, number, number]),
    // Recharts renders each rectangle at `height * t` and <Rectangle> returns
    // null at height 0, so with animation on, a bar does not EXIST in the DOM
    // until its rAF clock has run. That fails in a background or throttled
    // tab, in a screenshot pipeline, and under prefers-reduced-motion — the
    // regression pinned in src/test/recharts-bar-animation.test.tsx. <Line> is
    // unaffected, which is why this only ever showed up on bars.
    isAnimationActive: false,
  };

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface p-4', className)}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={data}
          layout={orientation === 'horizontal' ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 16, left: 4, bottom: 0 }}
        >
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={orientation === 'horizontal'} horizontal={orientation === 'vertical'} />
          {orientation === 'vertical' ? (
            <>
              <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'var(--border)' }} interval={xTickInterval} minTickGap={8} angle={-25} textAnchor="end" height={60} />
              {/* B7.2 FIX 3: use compact tick formatter on numeric axes.
                  The previous formatValue (e.g. "AED 10,000,000" for finance)
                  was wider than the 56px YAxis and got clipped, rendering as
                  "0,000,000". formatTick defaults to "10M" / "850K". */}
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickFormatter={formatTick} tickLine={false} axisLine={{ stroke: 'var(--border)' }} width={56} />
            </>
          ) : (
            <>
              <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickFormatter={formatTick} tickLine={false} axisLine={{ stroke: 'var(--border)' }} />
              <YAxis type="category" dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'var(--border)' }} width={120} interval={0} />
            </>
          )}
          <Tooltip
            cursor={{ fill: 'var(--bg-elevated)' }}
            contentStyle={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--text-primary)',
            }}
            formatter={(value: number) => formatValue(value)}
          />
          <Bar dataKey="value" {...barProps}>
            {data.map((d, idx) => (
              <Cell key={idx} fill={d.fill ?? defaultFill} />
            ))}
            {labels && orientation === 'horizontal' ? (
              <LabelList dataKey="value" content={horizontalLabelContent as never} />
            ) : labels ? (
              <LabelList
                dataKey="value"
                position="top"
                formatter={formatValue as (v: unknown) => string}
                style={{ fill: 'var(--text-secondary)', fontSize: 11 }}
              />
            ) : null}
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}
